import React, { useMemo } from 'react';
import { VisualizationEvent } from '../types';
import { PITCH_WIDTH, PITCH_HEIGHT, RotationAngle } from '../utils';

interface GridPlotProps {
  events: VisualizationEvent[];
  rotation?: RotationAngle;
  scale?: number;
  colorBy?: 'team' | 'stat_type';
  teamColors?: Record<string, string>;
  statTypeColors?: Record<string, string>;
  gridSize?: number; // Number of cells per axis (e.g., 10 = 10x10 grid)
  showLabels?: boolean; // Whether to show count labels in cells
  opacity?: number; // Base opacity for cells (0-1)
  whiteBackground?: boolean; // Whether the pitch has white background (affects text color)
  pointSelection?: ('source' | 'destination')[]; // Which point(s) to use from position_data
}

/**
 * Grid plot visualization component
 * Divides the pitch into a grid and counts events in each cell
 */
const GridPlot: React.FC<GridPlotProps> = ({
  events,
  rotation = 0,
  scale = 1.0,
  colorBy = 'team',
  teamColors = {},
  statTypeColors = {},
  gridSize = 10,
  showLabels = true,
  opacity = 0.6,
  whiteBackground = false,
  pointSelection = ['source'],
}) => {
  // Default team colors
  const defaultTeamColors: Record<string, string> = {
    default: '#3B82F6', // Blue
  };

  // Get color for an event based on colorBy setting
  const getEventColor = (event: VisualizationEvent): string => {
    switch (colorBy) {
      case 'team':
        return teamColors[event.team_id] || defaultTeamColors.default;
      case 'stat_type':
        // Use team-specific color if set (key format: `${statTypeId}-${teamId}`)
        // Fall back to stat-type-only color, then default
        if (event.team_id) {
          const teamSpecificKey = `${event.stat_type_id}-${event.team_id}`;
          if (statTypeColors[teamSpecificKey]) {
            return statTypeColors[teamSpecificKey];
          }
        }
        return statTypeColors[event.stat_type_id] || defaultTeamColors.default;
      default:
        return defaultTeamColors.default;
    }
  };

  // Calculate cell dimensions
  const cellWidth = PITCH_WIDTH / gridSize;
  const cellHeight = PITCH_HEIGHT / gridSize;

  // Helper function to blend two colors
  const blendColors = (color1: string, color2: string, ratio: number): string => {
    // Convert hex to RGB
    const hexToRgb = (hex: string): [number, number, number] => {
      const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
      return result
        ? [parseInt(result[1], 16), parseInt(result[2], 16), parseInt(result[3], 16)]
        : [0, 0, 0];
    };
    
    const rgb1 = hexToRgb(color1);
    const rgb2 = hexToRgb(color2);
    
    const r = Math.round(rgb1[0] * (1 - ratio) + rgb2[0] * ratio);
    const g = Math.round(rgb1[1] * (1 - ratio) + rgb2[1] * ratio);
    const b = Math.round(rgb1[2] * (1 - ratio) + rgb2[2] * ratio);
    
    return `#${[r, g, b].map(x => {
      const hex = x.toString(16);
      return hex.length === 1 ? '0' + hex : hex;
    }).join('')}`;
  };

  // Group events by cell and aggregate counts, blending colors when multiple teams/colors in same cell
  const gridData = useMemo(() => {
    // cellKey -> { totalCount, colorCounts: Map<color, count> }
    const cellData = new Map<string, { totalCount: number; colorCounts: Map<string, number> }>();

    events.forEach((event) => {
      if (!event.position_data || event.position_data.length === 0) return;
      
      const color = getEventColor(event);
      
      // Determine which points to use based on pointSelection
      let pointsToUse: Array<{ x: number; y: number }> = [];
      
      const hasSource = pointSelection.includes('source');
      const hasDestination = pointSelection.includes('destination');
      
      if (hasSource && hasDestination) {
        // Use both first and last points (if different)
        const first = event.position_data[0];
        const last = event.position_data[event.position_data.length - 1];
        if (first.x !== last.x || first.y !== last.y) {
          pointsToUse = [first, last];
        } else {
          pointsToUse = [first];
        }
      } else if (hasSource) {
        // Use only the first point
        pointsToUse = [event.position_data[0]];
      } else if (hasDestination) {
        // Use only the last point
        pointsToUse = [event.position_data[event.position_data.length - 1]];
      } else {
        // Default to first point if nothing selected
        pointsToUse = [event.position_data[0]];
      }

      // Process each point
      pointsToUse.forEach((point) => {
        // Calculate which cell this point belongs to
        const cellX = Math.floor(point.x / cellWidth);
        const cellY = Math.floor(point.y / cellHeight);
        
        // Clamp to valid grid bounds
        const clampedX = Math.max(0, Math.min(gridSize - 1, cellX));
        const clampedY = Math.max(0, Math.min(gridSize - 1, cellY));
        
        const cellKey = `${clampedX},${clampedY}`;

        if (!cellData.has(cellKey)) {
          cellData.set(cellKey, { totalCount: 0, colorCounts: new Map() });
        }
        
        const cell = cellData.get(cellKey)!;
        cell.totalCount += 1;
        cell.colorCounts.set(color, (cell.colorCounts.get(color) || 0) + 1);
      });
    });

    return cellData;
  }, [events, colorBy, teamColors, statTypeColors, cellWidth, cellHeight, gridSize, pointSelection]);

  // Find max count across all cells for normalization
  const maxCount = useMemo(() => {
    let max = 0;
    gridData.forEach((cell) => {
      if (cell.totalCount > max) max = cell.totalCount;
    });
    return max;
  }, [gridData]);

  // Generate grid cells with blended colors
  const gridCells = useMemo(() => {
    const cells: Array<{
      x: number;
      y: number;
      width: number;
      height: number;
      color: string;
      count: number;
      cellKey: string;
    }> = [];

    gridData.forEach((cell, cellKey) => {
      const [cellX, cellY] = cellKey.split(',').map(Number);
      
      // Determine the color for this cell
      // If multiple colors, blend them based on their proportions
      let finalColor = defaultTeamColors.default;
      if (cell.colorCounts.size === 1) {
        // Single color
        finalColor = Array.from(cell.colorCounts.keys())[0];
      } else if (cell.colorCounts.size > 1) {
        // Multiple colors - blend them
        const colors = Array.from(cell.colorCounts.entries());
        // Sort by count (descending) to blend from most to least
        colors.sort((a, b) => b[1] - a[1]);
        
        // Start with the dominant color
        finalColor = colors[0][0];
        
        // Blend with other colors based on their proportions
        for (let i = 1; i < colors.length; i++) {
          const ratio = colors[i][1] / cell.totalCount;
          finalColor = blendColors(finalColor, colors[i][0], ratio);
        }
      }

      cells.push({
        x: cellX * cellWidth,
        y: cellY * cellHeight,
        width: cellWidth,
        height: cellHeight,
        color: finalColor,
        count: cell.totalCount,
        cellKey: cellKey,
      });
    });

    return cells;
  }, [gridData, cellWidth, cellHeight]);

  return (
    <g id="grid-plot">
      {gridCells.map((cell) => {
        // Calculate opacity based on count (normalized to max count)
        const normalizedCount = maxCount > 0 ? cell.count / maxCount : 0;
        const cellOpacity = opacity * (0.3 + 0.7 * normalizedCount); // Range from 0.3*opacity to opacity

        return (
          <g key={cell.cellKey}>
            {/* Grid cell rectangle */}
            <rect
              x={cell.x}
              y={cell.y}
              width={cell.width}
              height={cell.height}
              fill={cell.color}
              opacity={cellOpacity}
              stroke="#ffffff"
              strokeWidth="1"
              strokeOpacity="0.3"
            />
            {/* Count label */}
            {showLabels && cell.count > 0 && (
              <text
                x={cell.x + cell.width / 2}
                y={cell.y + cell.height / 2}
                textAnchor="middle"
                dominantBaseline="middle"
                fill={whiteBackground ? "#000000" : "#ffffff"}
                fontSize={Math.min(cell.width, cell.height) * 0.3}
                fontWeight="bold"
                stroke={whiteBackground ? "#ffffff" : "#000000"}
                strokeWidth={whiteBackground ? "0.3" : "0.5"}
                strokeOpacity={whiteBackground ? "0.9" : "0.8"}
                style={{ pointerEvents: 'none', userSelect: 'none' }}
              >
                {cell.count}
              </text>
            )}
          </g>
        );
      })}
    </g>
  );
};

export default GridPlot;

